<template>
  <div class="subject-level-and-stream-selection">
    <div class="selection-wrapper">
      <dl class="field-set type-dropdown">
        <dt class="field-label">
          <span v-if="showSubjectLevelLabel">{{ t("search_filter_label_subject") }}</span>
        </dt>
        <dd class="field-input">
          <multi-select
            v-if="isMultiSelect"
            :kind="selectionKind"
            :disabled="isSubjectDisabled"
            class="subject-filter"
            :label="subjectFilterLabel"
            selection-feedback="fixed"
            :options="filteredSubjects"
            :value="subjectsSelected as unknown[]"
            :hide-clear-button="hideMultiSelectClearButton"
            @change="handleSubjectChange"
          />

          <combo-box
            v-else
            :kind="selectionKind"
            :disabled="isSubjectDisabled"
            :auto-filter="true"
            class="subject-filter"
            :label="subjectFilterLabel"
            :options="filteredSubjects"
            :value="subjectSelected"
            :invalid-message="subjectEmptyErrorMsg"
            @change="handleSubjectChange"
          />
        </dd>
      </dl>

      <dl class="field-set type-dropdown">
        <dt class="field-label">
          <span v-if="showSubjectLevelLabel">{{ t("search_filter_label_level") }}</span>
        </dt>
        <dd class="field-input">
          <multi-select
            v-if="isMultiSelect"
            :kind="selectionKind"
            :disabled="isLevelDisabled"
            class="level-course-filter"
            :label="levelAndCourseFilterLabel"
            selection-feedback="fixed"
            :options="levelAndStreams"
            :value="levelsSelected as unknown[]"
            :hide-clear-button="hideMultiSelectClearButton"
            @change="handleLevelAndCourseChange"
          />

          <combo-box
            v-else
            :kind="selectionKind"
            :disabled="isLevelDisabled"
            :auto-filter="true"
            class="level-course-filter"
            :label="levelAndCourseFilterLabel"
            :options="levelAndStreams"
            :value="levelSelected"
            :invalid-message="levelEmptyErrorMsg"
            @change="handleLevelAndCourseChange"
          />
        </dd>
      </dl>
    </div>

    <cv-button
      v-if="showDeleteRowButton"
      icon-button
      class="icon-button"
      kind="ghost"
      @click="handleDeleteSubjectLevel"
    >
      <svg-icon name="Trash24" kind="action" />
    </cv-button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { isNull } from "@utils/var.check";
import { useI18n } from "vue-i18n";
import submitLessonValidationStore from "@modules/editor/components/lesson/SubmitLessonValidationStore";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import ComboBox from "@plugins/common/components/ComboBox.vue";
import MultiSelect from "@plugins/common/components/MultiSelect.vue";
import { ComboBoxDropdownOption } from "@plugins/common/types.ts";
import {
  applySubjectFilterMode,
  SUBJECT_FILTER_MODE,
  SubjectFilterMode,
} from "@utils/subject.utils";

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    subjects?: ComboBoxDropdownOption[];
    levelAndStreams?: ComboBoxDropdownOption[];
    initSubjectSelected?: string;
    initLevelSelected?: string;
    initSubjectsSelected?: string[];
    initLevelsSelected?: string[];
    isSubjectDisabled?: boolean;
    isLevelDisabled?: boolean;
    hideMultiSelectClearButton?: boolean;
    isMultiSelect?: boolean;
    selectionKind?: string;
    showSubjectLevelLabel?: boolean;
    showDeleteRowButton?: boolean;
    subjectFilterMode?: SubjectFilterMode;
  }>(),
  {
    subjects: () => [],
    levelAndStreams: () => [],
    initSubjectSelected: "",
    initLevelSelected: "",
    initSubjectsSelected: () => [],
    initLevelsSelected: () => [],
    isSubjectDisabled: false,
    isLevelDisabled: false,
    hideMultiSelectClearButton: true,
    isMultiSelect: true,
    selectionKind: "gray",
    showSubjectLevelLabel: true,
    showDeleteRowButton: false,
    subjectFilterMode: undefined,
  },
);

const emit = defineEmits<{
  (e: "selectionUpdate", payload: { type: "subject" | "level"; data: string | string[] }): void;
  (
    e: "deleteSubjectLevel",
    payload: { levelAndStreamId: string | string[]; subjectId: string | string[] },
  ): void;
}>();

const subjectsSelected = ref<string | string[]>([]);
const levelsSelected = ref<string | string[]>([]);
const subjectSelected = ref<string>("");
const levelSelected = ref<string>("");

subjectsSelected.value = props.initSubjectsSelected;
levelsSelected.value = props.initLevelsSelected;
subjectSelected.value = props.initSubjectSelected;
levelSelected.value = props.initLevelSelected;

watch(
  () => props.initSubjectsSelected,
  () => {
    subjectsSelected.value = props.initSubjectsSelected;
  },
  { deep: true },
);

watch(
  () => props.initLevelsSelected,
  () => {
    levelsSelected.value = props.initLevelsSelected;
  },
  { deep: true },
);

watch(
  () => props.initSubjectSelected,
  () => {
    subjectSelected.value = props.initSubjectSelected;
  },
  { deep: true },
);

watch(
  () => props.initLevelSelected,
  () => {
    levelSelected.value = props.initLevelSelected;
  },
  { deep: true },
);

const NO_SELECTION: string[] = [];

const filteredSubjects = computed<ComboBoxDropdownOption[]>(() => {
  if (!props.subjectFilterMode) {
    return props.subjects;
  }

  // Only active-plus-selected consults the selection. Short-circuiting first keeps the
  // other modes from taking a reactive dependency on it, which would hand the dropdown a
  // fresh options array on every pick and force it to re-render the whole list.
  if (props.subjectFilterMode !== SUBJECT_FILTER_MODE.ACTIVE_PLUS_SELECTED) {
    return applySubjectFilterMode(props.subjects, props.subjectFilterMode, NO_SELECTION);
  }

  const rawSelected = props.isMultiSelect ? subjectsSelected.value : subjectSelected.value;
  const selectedValues = (Array.isArray(rawSelected) ? rawSelected : [rawSelected]).filter(Boolean);

  return applySubjectFilterMode(props.subjects, props.subjectFilterMode, selectedValues);
});

const subjectFilterLabel = computed(() => t("search_filter_label_subject_placeholder"));
const levelAndCourseFilterLabel = computed(() => t("search_filter_label_level_placeholder"));

const subjectEmptyErrorMsg = computed(() => {
  const error = submitLessonValidationStore.getSubmissionValidationErrors();
  return error?.subjectEmpty || "";
});

const levelEmptyErrorMsg = computed(() => {
  const error = submitLessonValidationStore.getSubmissionValidationErrors();
  return error?.levelEmpty || "";
});

function handleSubjectChange(data: unknown) {
  const isEmptySelection =
    isNull(data) || (Array.isArray(data) ? data.length === 0 : (data as string).length === 0);

  let selection: string | string[];
  if (props.isMultiSelect) {
    subjectsSelected.value = isEmptySelection ? [] : (data as string[]);
    selection = subjectsSelected.value;
  } else {
    subjectSelected.value = isEmptySelection ? "" : (data as string);
    selection = subjectSelected.value;
  }
  emit("selectionUpdate", { type: "subject", data: selection });
}

function handleLevelAndCourseChange(data: unknown) {
  const isEmptySelection =
    isNull(data) || (Array.isArray(data) ? data.length === 0 : (data as string).length === 0);

  let selection: string | string[];
  if (props.isMultiSelect) {
    levelsSelected.value = isEmptySelection ? [] : (data as string[]);
    selection = levelsSelected.value;
  } else {
    levelSelected.value = isEmptySelection ? "" : (data as string);
    selection = levelSelected.value;
  }
  emit("selectionUpdate", { type: "level", data: selection });
}

function handleDeleteSubjectLevel() {
  emit("deleteSubjectLevel", {
    levelAndStreamId: props.isMultiSelect ? levelsSelected.value : levelSelected.value,
    subjectId: props.isMultiSelect ? subjectsSelected.value : subjectSelected.value,
  });
}
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/spacing";
.subject-level-and-stream-selection {
  > .field-set {
    &.type-dropdown {
      > .field-label {
        flex: 0 0 3rem;

        @include media("desktop") {
          flex: 0 1 auto;
        }
      }
    }
  }
}
</style>
